import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Section } from '../section'
import { icons } from '../../../../../../utils/icons'
import { useTranslation } from 'react-i18next'
import { NoResponse } from '../../../../../../components/errors/noResponse'
import EvidenceDetail from '../evidenceDetail'
import { useEvidences } from '../../../../../../hooks/evidences/useEvidences'
import { Evidence } from '../../../../../../service/reportFolio/response'
import { Answer, DetailCreation, DetailQuestion, FullAnswer } from '../../../../../../service/actionPlans/actionPlanById'
import { useStyles } from './styles'
import { CatalogAnswerDetail, CatalogAnswerBadge } from '../catalogAnswerDetail'
import { useEvidenceDownload } from '../../../../../calendar/pages/detail/hooks'
import { SnackSimple } from '../../../../../../components'

export type QuestionWithEvidence = {
  question: Answer
  answerText?: string
  fullAnswer?: FullAnswer[]
  evidences?: Evidence[]
}

export type QuestionWithEvidencesSectionProps = {
  detailCreation?: DetailCreation
  detailQuestions?: DetailQuestion[]
  questions?: QuestionWithEvidence[]
  id?: number
  showGlobalActions?: boolean
}

function QuestionWithEvidencesSection({ detailCreation, detailQuestions, questions, id, showGlobalActions = false }: QuestionWithEvidencesSectionProps) {
  const { t } = useTranslation()
  const styles = useStyles()

  const questionList = useMemo<QuestionWithEvidence[]>(() => {
    if (questions?.length) return questions
    if (detailQuestions?.length) {
      return detailQuestions.map((detailQuestion) => {
        const selectedAnswers = detailQuestion.answers?.filter((answer) => answer.selected) ?? []
        return {
          question: {
            id: detailQuestion.questionId,
            title: detailQuestion.questionName,
            selected: selectedAnswers.length > 0,
          },
          answerText: selectedAnswers.map((answer) => answer.title).join(' / ') || undefined,
          fullAnswer: selectedAnswers[0]?.fullAnswer,
          evidences: detailQuestion.evidences as Evidence[],
        }
      })
    }
    return detailCreation?.answers?.map((question) => ({ question })) ?? []
  }, [questions, detailQuestions, detailCreation])

  const [expandedQuestions, setExpandedQuestions] = useState<boolean[]>(() =>
    questionList.map((item, index) => index === 0 && !!item.evidences?.length)
  )
  const [expandAllEvidences, setExpandAllEvidences] = useState(false)

  useEffect(() => {
    setExpandedQuestions(questionList.map((item, index) => index === 0 && !!item.evidences?.length))
    setExpandAllEvidences(false)
  }, [questionList])

  const allExpanded = expandedQuestions.every(Boolean)

  const toggleAll = () => {
    const newExpanded = !allExpanded
    setExpandedQuestions((prev) => prev.map(() => newExpanded))
    setExpandAllEvidences(newExpanded)
  }

  const toggleQuestion = (index: number) => {
    setExpandedQuestions((prev) =>
      prev.map((expanded, i) => (i === index ? !expanded : expanded))
    )
  }

  const allEvidences = useMemo(
    () => questionList.flatMap((item) => item.evidences ?? []),
    [questionList]
  )

  const {
    disable: disableDownloadAll,
    downloadEvidences: downloadAllEvidences,
    openSnackbar: openSnackbarAll,
    handleCloseSnackbar: handleCloseSnackbarAll,
  } = useEvidenceDownload(`evidencias de plan de acción ${id ?? ''}`, allEvidences)

  return (
    <Section
      icon={icons('paper')}
      title={t('actionPlan.questionsAndEvidences')}
      subtitle={detailCreation?.questionnaire ?  detailCreation.questionnaire : undefined}
    >
      <Box sx={styles.container}>
        {showGlobalActions && questionList.length > 0 && (
          <Box sx={styles.expandAllButtonContainer}>
            <Button variant='text_primary' onClick={toggleAll} disabled={!allEvidences.length} sx={{ padding: '0' }}>
              {allExpanded ? t('actionPlan.collapseAll') : t('schedule.buttons.expandAll')}
            </Button>
          </Box>
        )}

        {!!questionList.length && questionList.map((item, index) => (
          <QuestionCard
            key={item.question.id ?? index}
            {...item}
            id={id}
            isFirst={index === 0}
            showGlobalActions={showGlobalActions}
            expanded={expandedQuestions[index]}
            expandAllEvidences={expandAllEvidences}
            onToggle={() => toggleQuestion(index)}
          />
        ))}

        {!questionList.length && (
          <NoResponse label={t('reportFolio.detail.general.noInformation')} />
        )}

        {showGlobalActions && questionList.length > 0 && (
          <Box sx={styles.downloadAllButtonContainer}>
            <Button
              variant='text_primary'
              disabled={disableDownloadAll || !allEvidences.length}
              onClick={downloadAllEvidences}
              sx={{ padding: '0' }}
            >
              {t('actionPlan.downloadEvidences')}
            </Button>
          </Box>
        )}

        {showGlobalActions && (
          <SnackSimple
            close={handleCloseSnackbarAll}
            open={openSnackbarAll.open}
            type={openSnackbarAll.type as any}
            message={openSnackbarAll.message}
          />
        )}
      </Box>
    </Section>
  )
}

function QuestionCard({
  question,
  answerText,
  fullAnswer,
  evidences,
  isFirst,
  id,
  showGlobalActions,
  expanded,
  expandAllEvidences,
  onToggle,
}: QuestionWithEvidence & {
  isFirst?: boolean
  id?: number
  showGlobalActions?: boolean
  expanded?: boolean
  expandAllEvidences?: boolean
  onToggle?: () => void
}) {
  const { t } = useTranslation()
  const styles = useStyles()
  const { evidences: evidenceLeft, existEvidences } = useEvidences({ evidencesResponse: evidences })
  const { disable, downloadEvidences, openSnackbar, handleCloseSnackbar } = useEvidenceDownload(`evidencias de plan de acción ${id ?? ''}`, evidences)

  const evidenceDetailData = useMemo(
    () => ({
      image: evidenceLeft?.images,
      comment: evidenceLeft?.comments,
      documents: evidenceLeft?.documents,
      video: evidenceLeft?.video,
      sign: evidenceLeft?.sign,
    }),
    [evidenceLeft?.images, evidenceLeft?.comments, evidenceLeft?.documents, evidenceLeft?.video, evidenceLeft?.sign]
  )

  const handleAccordionChange = (_event: React.SyntheticEvent, _expanded: boolean) => {
    onToggle?.()
  }

  const accordionProps = showGlobalActions
    ? { expanded: !!expanded, onChange: handleAccordionChange }
    : { defaultExpanded: isFirst && !!evidences?.length }

  return (
    <Accordion sx={styles.accordion} disableGutters {...accordionProps}>
      <AccordionSummary expandIcon={existEvidences ? icons('arrow-down') : null} sx={styles.accordionSummary}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
            <Typography sx={styles.questionText}>{question.title ?? ''}</Typography>
            {!!fullAnswer?.length && (
              <CatalogAnswerBadge count={fullAnswer.length} />
            )}
          </Box>

          {!!answerText && !fullAnswer?.length && (
            <Typography sx={styles.answerText}>
              {t('actionPlan.answer')}: {answerText}
            </Typography>
          )}

          {!!fullAnswer?.length && (
            <CatalogAnswerDetail fullAnswer={fullAnswer} showPathLabel={false} />
          )}
        </Box>
      </AccordionSummary>

      {existEvidences && (
        <AccordionDetails sx={styles.accordionDetails}>
          <EvidenceDetail
            evidences={evidenceDetailData}
            existEvidences={existEvidences}
            containerSx={{ gap: 0 }}
            showExpandButton={!showGlobalActions}
            forceExpandAll={showGlobalActions ? expandAllEvidences : undefined}
          />
          {!showGlobalActions && (
            <Box sx={styles.downloadButtonContainer}>
              <Button variant='text_primary' disabled={disable} onClick={downloadEvidences}>
                {t('actionPlan.downloadEvidences')}
              </Button>
            </Box>
          )}
        </AccordionDetails>
      )}

      {!showGlobalActions && (
        <SnackSimple close={handleCloseSnackbar} open={openSnackbar.open} type={openSnackbar.type as any} message={openSnackbar.message} />
      )}
    </Accordion>
  )
}

export { QuestionWithEvidencesSection }
